import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Download, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';

interface CodeRendererProps {
  artifact: Artifact;
}

export function CodeRenderer({ artifact }: CodeRendererProps) {
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  
  const codeContent = typeof artifact.data === 'string' 
    ? artifact.data 
    : String(artifact.data);

  const getLanguage = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'sql': 'sql',
      'json': 'json',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'md': 'markdown',
      'yml': 'yaml',
      'yaml': 'yaml',
      'xml': 'xml',
      'sh': 'bash',
      'bash': 'bash',
    };
    return languageMap[ext || ''] || 'text';
  };

  const language = getLanguage(artifact.name);
  const lines = codeContent.split('\n');

  const handleCopy = () => {
    navigator.clipboard.writeText(codeContent);
  };

  const handleDownload = () => {
    const blob = new Blob([codeContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!codeContent.trim()) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The code file is empty.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {language}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {lines.length} lines
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLineNumbers(!showLineNumbers)}
          >
            {showLineNumbers ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            Line Numbers
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </div>

      {/* Code Content */}
      <div className="border rounded-lg overflow-hidden bg-muted/30">
        <div className="max-h-96 overflow-auto">
          <div className="flex">
            {/* Line Numbers */}
            {showLineNumbers && (
              <div className="flex-shrink-0 bg-muted/50 border-r border-border px-3 py-4 text-xs text-muted-foreground font-mono select-none">
                {lines.map((_, index) => (
                  <div key={index} className="leading-6 text-right">
                    {index + 1}
                  </div>
                ))}
              </div>
            )}
            
            {/* Code */}
            <div className="flex-1 p-4">
              <pre className="text-sm font-mono leading-6 whitespace-pre overflow-x-auto">
                <code className={`language-${language}`}>
                  {codeContent}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* File Info */}
      <div className="text-xs text-muted-foreground">
        <div>File: {artifact.name}</div>
        <div>Size: {new Blob([codeContent]).size} bytes</div>
        {artifact.metadata?.encoding && (
          <div>Encoding: {artifact.metadata.encoding}</div>
        )}
      </div>
    </div>
  );
}